import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { BehaviorSubject, Observable } from 'rxjs';

import { ActionCommentRequestBody, ActionResponseBody, EntityType } from '@hm/core/models/action.interface';

import { environment } from '@hm/environments/environment';

import { DobrobutClinic, DrEleksVisit } from '@lib/core/models/dobrobut-entities.interface';
import { MemberProfile } from '@lib/core/models/member.interface';
import { PagedResponse } from '@lib/core/models/paged-response';
import { PharmacyOrder } from '@lib/core/models/pharmacy.interface';
import { ServiceDictionary } from '@lib/core/models/protocol.interface';
import { Appointment, AppointmentSearchModel, BindAppointmentRequestModel, ExistingAppointmentSearchRequest, Visit } from '@lib/core/models/visit.interface';

import { Partner, PartnerLocation, PartnerLocationsSearchPayload, PartnerSearchPayload } from '../models/partner.interface';

@Injectable({
  providedIn: 'root',
})
export class VisitsService {
  private readonly http = inject(HttpClient);

  selectedEleksVisit$ = new BehaviorSubject<DrEleksVisit | null>(null);
  existingAppointment$ = new BehaviorSubject<Appointment | null>(null);

  getTodayAppointments(paging?: { page: number; pageSize: number }) {
    const options = {
      params: new HttpParams().set('Page', paging?.page).set('PageSize', paging?.pageSize),
    };

    return this.http.get<PagedResponse<Visit>>(`${environment.healthManagerApiUrl}/members/actions/today-appointments`, paging ? options : {});
  }

  getClientsAppointments(clientId: string, guaranteeId: string) {
    return this.http.get<Visit[]>(`${environment.healthManagerApiUrl}/members/${clientId}/guarantee-applications/${guaranteeId}/actions`);
  }

  createAppointment(clientId: string, actionId: string, data: Partial<BindAppointmentRequestModel>) {
    return this.http.post<Visit>(`${environment.healthManagerApiUrl}/members/${clientId}/actions/${actionId}/appointment`, data);
  }

  bindEleksAppointment(actionId: string, data: BindAppointmentRequestModel) {
    return this.http.post(`${environment.healthManagerApiUrl}/members/actions/${actionId}/appointment/bind`, data);
  }

  updateBindEleksAppointment(appointmentId: string, data: BindAppointmentRequestModel) {
    return this.http.put(`${environment.healthManagerApiUrl}/members/appointments/${appointmentId}/bind`, data);
  }

  searchAllVisits(searchModel: AppointmentSearchModel) {
    return this.http.post<PagedResponse<Visit>>(`${environment.healthManagerApiUrl}/members/actions/search`, searchModel);
  }

  searchMemberAppointments(searchModel: AppointmentSearchModel) {
    return this.http.post<PagedResponse<Visit>>(`${environment.healthManagerApiUrl}/members/actions/appointments/search`, searchModel);
  }

  getMemberProfileFromAction(actionId: string) {
    return this.http.get<MemberProfile>(`${environment.healthManagerApiUrl}/members/actions/${actionId}`);
  }

  getDobrobutClinics() {
    return this.http.get<DobrobutClinic[]>(`${environment.healthManagerApiUrl}/members/dobrobut-clinics`);
  }

  searchDobrobutMedicalServices(searchString: string) {
    const options = { nameUa: searchString };
    return this.http.post<PagedResponse<ServiceDictionary>>(`${environment.healthManagerApiUrl}/dictionaries/dobrobut-services`, options);
  }

  getPharmacyOrderByActionId(actionId: string) {
    return this.http.get<PharmacyOrder>(`${environment.healthManagerApiUrl}/members/actions/${actionId}/pharmacy-order`);
  }

  getDrEleksVisits(userId: string) {
    return this.http.get<DrEleksVisit[]>(`${environment.healthManagerApiUrl}/members/by-user/${userId}/appointments/suggestions`);
  }

  searchExistingAppointments(requestBody: ExistingAppointmentSearchRequest): Observable<PagedResponse<Appointment>> {
    return this.http.post<PagedResponse<Appointment>>(`${environment.healthManagerApiUrl}/members/appointments/search`, requestBody);
  }

  createManualAppointment(value: unknown): Observable<{ id: string }> {
    return this.http.post<{ id: string }>(`${environment.healthManagerApiUrl}/members/appointments`, value);
  }

  updateManualAppointment(appointmentId: string, value: unknown): Observable<{ id: string }> {
    return this.http.put<{ id: string }>(`${environment.healthManagerApiUrl}/members/appointments/${appointmentId}`, value);
  }

  cancelManualAppointment(appointmentId: string): Observable<{ cancellationStatusFailedToSync: boolean }> {
    return this.http.post<{ cancellationStatusFailedToSync: boolean }>(`${environment.healthManagerApiUrl}/members/appointments/${appointmentId}/cancel`, {});
  }

  getPartnersCities(): Observable<string[]> {
    return this.http.get<string[]>(`${environment.healthManagerApiUrl}/partners/cities`);
  }

  searchPartners(searchQuery: PartnerSearchPayload): Observable<PagedResponse<Partner>> {
    return this.http.post<PagedResponse<Partner>>(`${environment.healthManagerApiUrl}/partners/search`, searchQuery);
  }

  searchPartnersLocations(partnerId: string, searchQuery: PartnerLocationsSearchPayload): Observable<PagedResponse<PartnerLocation>> {
    return this.http.post<PagedResponse<PartnerLocation>>(`${environment.healthManagerApiUrl}/partners/${partnerId}/locations/search`, searchQuery);
  }

  getPartnersLocations(partnerId: string): Observable<PartnerLocation[]> {
    return this.http.get<PartnerLocation[]>(`${environment.healthManagerApiUrl}/partners/${partnerId}/locations`);
  }

  completeManualAppointment(appointmentId: string): Observable<unknown> {
    const requestBody = {
      reason: 'Completed by health manager.',
      isMedicalDataExpected: true,
      didAppear: true,
    };
    return this.http.put<unknown>(`${environment.healthManagerApiUrl}/members/appointments/${appointmentId}/complete`, requestBody);
  }

  getAppointmentNote(entityId: string): Observable<ActionResponseBody> {
    return this.http.get<ActionResponseBody>(`${environment.healthManagerApiUrl}/actions/notes/${entityId}`);
  }

  createAppointmentNote(action: ActionCommentRequestBody): Observable<ActionResponseBody> {
    return this.http.post<ActionResponseBody>(`${environment.healthManagerApiUrl}/actions`, action);
  }

  updateAppointmentNote(actionId: string, actionContent: string): Observable<ActionResponseBody> {
    const requestBody = {
      content: actionContent,
    };
    return this.http.patch<ActionResponseBody>(`${environment.healthManagerApiUrl}/actions/${actionId}`, requestBody);
  }
}
